<template>
  <div class="grua-detail-page">
    <!-- Barra superior con título y acciones -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles de Empresa de Grúas</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn icon @click="showGruaForm(grua)" v-if="!loading && canEditGrua">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles de la empresa de grúas...
      </div>
    </div>

    <template v-else>
      <v-container>
        <v-row>
          <!-- Panel principal de información de la empresa -->
          <v-col cols="12" md="8" lg="7">
            <v-card class="elevation-1">
              <!-- Cabecera con datos principales -->
              <div class="grua-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="64" class="mr-4">
                    <v-icon size="36" dark>mdi-tow-truck</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ grua.company }}</div>
                    <div class="subtitle-1 grey--text">
                      CIF: {{ grua.cif || 'No especificado' }}
                    </div>
                  </div>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Información de la empresa -->
              <v-card-text class="py-4">
                <v-row>
                  <!-- Columna Información básica -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-domain</v-icon>
                      Información de la empresa
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-office-building</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Nombre de empresa</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ grua.company }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-identifier</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">CIF</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ grua.cif || 'No especificado' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="grua.address" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-map-marker-radius</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dirección</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ grua.address }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="grua.website" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-web</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Sitio web</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="grua.website" target="_blank" class="text-decoration-none">
                              {{ grua.website }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <!-- Información del dealer (solo para colaboradores) -->
                      <v-list-item v-if="grua.dealership && $store.getters.HAS_ROLE(['colaborator'])" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">{{ grua.dealership.is_colaborador ? 'mdi-home' : 'mdi-store' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dealer asignado</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1 d-flex align-center">
                            <v-avatar v-if="grua.dealership.imageUrl" size="20" class="mr-2">
                              <img :src="grua.dealership.imageUrl.url" alt="Logo dealer">
                            </v-avatar>
                            {{ grua.dealership.name }}
                            <v-chip v-if="grua.dealership.is_colaborador" x-small class="ml-2" color="primary">
                              Propio
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <!-- Columna Información de contacto -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-card-account-phone</v-icon>
                      Información de contacto
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="grua.phone" :href="`tel:${grua.phone}`" class="text-decoration-none">
                              {{ grua.phone }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Correo electrónico</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="grua.email" :href="`mailto:${grua.email}`" class="text-decoration-none">
                              {{ grua.email }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="grua.contact_person" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-account-tie</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Persona de contacto</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ grua.contact_person }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="grua.secondary_phone" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone-alert</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono secundario</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="`tel:${grua.secondary_phone}`" class="text-decoration-none">
                              {{ grua.secondary_phone }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="grua.dealer && $store.getters.HAS_ROLE(['colaborator'])" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">{{ grua.dealer.is_own ? 'mdi-home' : 'mdi-store' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dealer asignado</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ grua.dealer.name }}
                            <v-chip v-if="grua.dealer.is_own" x-small class="ml-1" color="primary">Propio</v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición -->
    <grua-form v-if="visibleGruaForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleGruaForm = false"></grua-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GruaForm from './GruaForm.vue'
import GruaService from '@/services/GruaService'
import moment from 'moment'

export default {
  components: {
    GruaForm
  },
  computed: {
    ...mapGetters(['CURRENT_USER_ROLES', 'HAS_PERMISSION']),
    canManageGruas() {
      return !this.CURRENT_USER_ROLES.includes('mechanic')
    },

    // Permisos de grúas
    canEditGrua() {
      return this.HAS_PERMISSION('editar_grua')
    }
  },
  data () {
    return {
      loading: true,
      grua: null,
      visibleGruaForm: false,
      editItem: null,

      // Valores de ejemplo para estadísticas - deberían cargarse desde la API
      movimientosCount: 0,
      ultimoServicio: '-'
    }
  },
  async created () {
    await this.loadGruaDetails()
  },
  methods: {
    async loadGruaDetails () {
      try {
        this.loading = true
        const gruaId = this.$route.params.grua_id
        const { data } = await GruaService.view({ id: gruaId, expand: 'dealership' })
        this.grua = data.data

        // Aquí podrías cargar las estadísticas de la grúa
        // await this.loadGruaStats(gruaId)
      } catch (error) {
        console.error('Error al cargar los detalles de la empresa de grúas:', error)
        this.$toast.error('Error al cargar los detalles de la empresa de grúas')
      } finally {
        this.loading = false
      }
    },

    showGruaForm (item) {
      this.editItem = item
      this.visibleGruaForm = true
    },

    updateDataTable () {
      this.visibleGruaForm = false
      this.loadGruaDetails()
      this.$toast.success('Empresa de grúas actualizada con éxito')
    },

    formatDate (dateString) {
      if (!dateString) return 'No especificado'
      return moment(dateString).format('DD/MM/YYYY')
    }
  }
}
</script>

<style scoped>
.grua-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.v-list-item {
  min-height: 0;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Transiciones y efectos */
.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}
</style>
